import React, { useEffect, useState } from "react";
import { fetchfreeQtyReport, fetchGodownList } from "../services/freeQtyReportService";
import DashboardLayout from "../layouts/DashboardLayout";
import ExcelExport from "../components/ExcelExport";
import { freeQuantityReportColumns } from "../config/reportColumns";
import { FreeQtyReportTable } from "../components/ReportTable";
import { fetchGetYear } from "../services/getYear";
import SearchableDropdown from "../components/SearchableDropdown";
import FilterBar from "../components/FilterBar";

const FreeQuantityReport = ({ onLogout }) => {

  const [reportData, setReportData] = useState([]);
  const [yearList, setYearList] = useState([]);

  // 🆕 All three filters are now arrays (multi-select)
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedGodowns, setSelectedGodowns] = useState([]);
  const [selectedPackSizes, setSelectedPackSizes] = useState([]);

  // 🆕 Godown options, fetched from DB (not hardcoded)
  const [godownList, setGodownList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempFromDate, setTempFromDate] = useState("");
  const [tempToDate, setTempToDate] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");
  const [isApplied, setIsApplied] = useState(false);

  const yearOptions = yearList.map((year) => ({
    label: year.toString(),
    value: year,
  }));

  const godownOptions = godownList.map((g) => ({
    label: g,
    value: g,
  }));

  // 🆕 Distinct pack sizes present in the currently loaded data
  const packSizeOptions = React.useMemo(() => {
    const unique = [...new Set(
      reportData.map((r) => r.pack_sizes).filter(Boolean)
    )].sort();
    return unique.map((p) => ({ label: p, value: p }));
  }, [reportData]);

  const isWithinBDFinancialYear = (from, to, year) => {
    const fyStart = new Date(year, 6, 1);     // 1 July
    const fyEnd = new Date(year + 1, 5, 30);  // 30 June
    return from >= fyStart && to <= fyEnd;
  };

  // ── Load available years ──
  useEffect(() => {
    fetchGetYear()
      .then((years) => {
        if (Array.isArray(years) && years.length > 0) {
          setYearList(years);
          setSelectedYears([Number(years[0])]); // default latest FY
        }
      })
      .catch(() => {
        setError("Failed to load year list");
      });
  }, []);

  // ── Load available godowns (derived from DB, once) ──
  useEffect(() => {
    fetchGodownList()
      .then((godowns) => setGodownList(godowns))
      .catch(() => {
        // Non-fatal — dropdown just stays empty/disabled if this fails
        setGodownList([]);
      });
  }, []);

  useEffect(() => {
    if (!selectedYears.length) return;

    // Clear any previously applied custom date filter
    setAppliedFromDate("");
    setAppliedToDate("");
    setIsApplied(false);

    loadReport();
  }, [selectedYears, selectedGodowns]);

  /*
   * 🆕 Fetch report for every selected year and merge the rows.
   * Years are NOT combined server-side, because the report's monthly
   * columns (jul_free_qty ... jun_free_qty) are only meaningful within
   * a single July–June financial year — merging years server-side
   * would silently mix two different "Julys" into one column.
   */
  const loadReport = async (opts = {}) => {
    if (!selectedYears.length) return;
    try {
      setLoading(true);
      setError(null);

      const useCustomDates = selectedYears.length === 1 && opts.from && opts.to;

      const responses = await Promise.all(
        selectedYears.map((yr) =>
          fetchfreeQtyReport({
            year: yr,
            godown: selectedGodowns,
            from: useCustomDates ? opts.from : undefined,
            to: useCustomDates ? opts.to : undefined,
          })
        )
      );

      const merged = responses.flatMap((res, idx) =>
        (res.data || []).map((row) => ({
          ...row,
          financial_year: `${selectedYears[idx]}-${selectedYears[idx] + 1}`,
        }))
      );

      setReportData(merged);
    } catch (err) {
      setError("Failed to load report data");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredReportData = reportData.filter((item) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !searchTerm ||
      item.products_name?.toLowerCase().includes(term) ||
      item.pack_sizes?.toLowerCase().includes(term);

    // 🆕 Pack size multi-select filter
    const matchesPackSize =
      !selectedPackSizes.length || selectedPackSizes.includes(item.pack_sizes);

    return matchesSearch && matchesPackSize;
  });

  return (
    <DashboardLayout onLogout={onLogout}>
      {/* 🆕 root is now an actual flex column, so flex-1/min-h-0 below work */}
      <div className="h-screen overflow-hidden bg-gray-100 flex flex-col">

        {/* HEADER */}
        <div className="shrink-0 bg-white p-2 border-b z-10 flex items-center justify-between">
          <h1 className="text-lg font-bold text-center flex-1">
            Free Quantity Report
          </h1>

          <ExcelExport
            data={filteredReportData}
            columns={freeQuantityReportColumns}
            reportTitle="Free Quantity Report"
            fileName="Free_Quantity_report.xlsx"
            reportFilters={{
              financialYears: selectedYears.length ? selectedYears.join(", ") : "",
              godowns: selectedGodowns.length ? selectedGodowns.join(", ") : "",
              packSizes: selectedPackSizes.length ? selectedPackSizes.join(", ") : "",
              // 🆕 Single combined "Date Range" row instead of separate From/To rows.
              // Falls back to the FY default range when no custom range was applied
              // and exactly one year is selected; blank (omitted) for multi-year.
              dateRange:
                appliedFromDate && appliedToDate
                  ? `${appliedFromDate} to ${appliedToDate}`
                  : selectedYears.length === 1
                    ? `${selectedYears[0]}-07-01 to ${selectedYears[0] + 1}-06-30`
                    : "",
            }}
            groupKeyFields={["products_name"]}
            hiddenRepeatKeys={["grand_total_qty", "grand_total_amount", "total_alt_qty", "products_name"]}
          />
        </div>

        {/* FILTER BAR */}
        <div className="shrink-0 bg-gray-100 px-4 py-2 border-b flex items-center justify-between gap-4 flex-wrap">

          {/* Left: Search Bar */}
          <div className="w-[320px] flex-shrink-0">
            <input
              type="text"
              placeholder="Search product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="
      w-full
      h-[42px]
      border
      border-gray-300
      rounded-md
      px-4
      text-sm
      bg-white
      focus:outline-none
      focus:ring-2
      focus:ring-blue-500
      focus:border-blue-500
    "
            />
          </div>

          <FilterBar
            fromDate={tempFromDate}
            setFromDate={setTempFromDate}
            toDate={tempToDate}
            setToDate={setTempToDate}
            onApply={async () => {
              if (!tempFromDate || !tempToDate) {
                alert("Please select both dates");
                return;
              }

              if (selectedYears.length !== 1) {
                alert("Custom date range works only when exactly one Year is selected.");
                return;
              }

              const from = new Date(tempFromDate);
              const to = new Date(tempToDate);

              if (!isWithinBDFinancialYear(from, to, selectedYears[0])) {
                alert("Please select date within the same Bangladesh Financial Year");
                return;
              }

              setAppliedFromDate(tempFromDate);
              setAppliedToDate(tempToDate);
              setIsApplied(true);

              await loadReport({ from: tempFromDate, to: tempToDate });
            }}
          />

          {/* {isApplied && selectedYears.length === 1 && (
            <p className="text-sm text-green-600">
              Showing data from {appliedFromDate} to {appliedToDate}
            </p>
          )} */}

          <div className="flex items-center gap-2">
            <SearchableDropdown
              label="Godown"
              options={godownOptions}
              value={selectedGodowns}
              onChange={setSelectedGodowns}
              disabled={godownOptions.length === 0}
              placeholder="All Godowns"
              isMulti={true}
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="w-[400px] flex-shrink-0">
              <SearchableDropdown
                label="Pack Size"
                options={packSizeOptions}
                value={selectedPackSizes}
                onChange={setSelectedPackSizes}
                disabled={packSizeOptions.length === 0}
                placeholder="All Pack Sizes"
                isMulti={true}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <SearchableDropdown
              label="Year"
              options={yearOptions}
              value={selectedYears}
              onChange={(vals) => setSelectedYears((vals || []).map(Number))}
              placeholder="Select Year"
              isMulti={true}
            />
          </div>

        </div>

        {/* 🆕 CONTENT — this is the piece that was actually broken.
             flex-1 + min-h-0 only work because the root above is now flex flex-col.
             This div reserves exactly "whatever space is left" and hands
             that bounded height down to the table. */}
        <div className="flex-1 min-h-0 bg-white p-2">
          {loading && (
            <p className="text-center py-10 text-gray-500">
              Loading report...
            </p>
          )}

          {error && (
            <p className="text-center py-10 text-red-500">
              {error}
            </p>
          )}

          {!loading && !error && (
            <div className="h-full">
              <FreeQtyReportTable
                reportData={filteredReportData}
                columns={freeQuantityReportColumns}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreeQuantityReport;